import {
  metaCacheLookup,
  metaCachePut,
  type CacheEntry,
  type MetaCachePolicy,
  type MetaCacheState,
} from '../cache/metaCache.ts';
import { shouldRecordWatched } from '../history/watched.ts';

/**
 * §8's resume position and the progress bar on a thumbnail, as one stored fact and four decisions.
 *
 * **The API stores no positions at all**, and that is not an omission this module works around: it is
 * what forces the module to exist. `GET /api/v1/auth/history` returns bare video ids with no
 * timestamps, no ordering by *when* and nothing resembling a playhead (API reference §8c), so
 * "continue where you left off" is ours to store or it does not exist. docs/specs/settings.md says
 * so in one line — *resume position stays local* — and its sync table's `save_player_pos` row is the switch
 * rather than the data: the account can hold *whether* to resume and never *where*.
 *
 * ### Why this is not the metadata cache, even though it borrows its policy
 *
 * `cache/metaCache.ts` owns LRU-on-last-use, the hard entry cap and wholesale eviction on a version
 * bump, and re-writing any of those here would be a second copy of a rule that is already right. So the
 * shape is `MetaCacheState<WatchProgressEntry>` and the folds are its folds. Two things about *this*
 * store are nonetheless different, and both would be bugs if they were left implicit:
 *
 * - **Losing an entry loses data.** A cached title costs one request to re-fetch; a position cannot be
 *   re-derived from anything, on this instance or any other. So the cap is generous, and a version bump
 *   is a decision to throw away every reader's positions rather than a free reset.
 * - **A stale entry is not a refresh, it is an expiry.** `metaCacheLookup` reports a hit past the TTL so
 *   the caller can render it and re-fetch it — right for a title, wrong for a playhead. There is nothing
 *   to re-fetch and a year-old position is not where anybody left off, so {@link watchProgressMark}
 *   treats `stale` as *no mark at all*. Same function, opposite reading, stated here because the
 *   difference is invisible at the call site.
 *
 * `touchMetaCache` is deliberately never called on this store either. `lastSeenEpochSec` is written
 * equal to `cachedAtEpochSec` on every write, so the LRU order *is* "oldest position first" — which is
 * the eviction anyone would ask for — and rendering a feed row does not write to storage. That last part
 * matters: a progress bar is drawn on every row of every listing page, and the video cache's own
 * `touch` had to be guarded against exactly this (`platform/videoMetaStore.ts`).
 */

/** Bumped → every stored position is dropped (R3/R5). Not free here; see the header. */
export const WATCH_PROGRESS_VERSION = 1;

/**
 * One video's playhead, as stored. ~150 bytes of JSON, against the 89–262 KB record it is about.
 *
 * `durationSec` is stored rather than looked up because the *fraction* has to be computable with no
 * network and no metadata cache hit: a feed row draws its bar before anything has been hydrated, and
 * the video record that would carry the length is the request R14 exists to avoid.
 */
export interface WatchProgressEntry extends CacheEntry {
  /** Where playback was, in seconds. */
  readonly positionSec: number;
  /** `null` when the source never reported one — a live stream, or a plan with no duration yet. */
  readonly durationSec: number | null;
  /**
   * Watched to the end (see {@link progressComplete}). Stored rather than recomputed on read so a
   * later build changing the threshold cannot retroactively un-finish a video somebody finished.
   */
  readonly complete: boolean;
}

export type WatchProgressStore = MetaCacheState<WatchProgressEntry>;

/**
 * Deliberately not `DEFAULT_META_CACHE_POLICY`.
 *
 * **2000 entries** rather than 1000: an entry is ~150 bytes, so the whole store is ~300 KB — the same
 * order as the video metadata cache while holding twice as many rows, because what it holds is smaller
 * *and* not re-fetchable. **A year** rather than 30 days, because a title going stale is a reason to
 * re-read it and a position going stale is a reason to forget it: 30 days would quietly drop the
 * half-finished three-hour documentary that is the whole point of the feature.
 */
export const WATCH_PROGRESS_POLICY: MetaCachePolicy = {
  maxEntries: 2000,
  ttlSec: 365 * 24 * 60 * 60,
};

/**
 * How close to the end counts as finished, as a fraction of the whole.
 *
 * Paired with {@link PROGRESS_COMPLETE_TAIL_SEC} and the two are combined by taking whichever lands
 * **later**, which is the only combination that is right at both ends of the corpus this app plays —
 * see {@link progressComplete}.
 */
export const PROGRESS_COMPLETE_FRACTION = 0.95;

/** How close to the end counts as finished, in seconds. */
export const PROGRESS_COMPLETE_TAIL_SEC = 15;

/**
 * How far playback must move before the position is written again.
 *
 * The Watch page samples `status()` four times a second for the transport bar, so an unthrottled
 * writer would serialise the whole store into `localStorage` 240 times a minute. Five seconds is the
 * granularity a resume is *useful* to — nobody notices returning five seconds early — and it makes the
 * write rate 12/minute for a video that is playing and zero for one that is paused.
 */
export const PROGRESS_WRITE_INTERVAL_SEC = 5;

/**
 * The smallest movement worth a write when something is being flushed rather than sampled — a pause, a
 * navigation away. Not zero, so a flush that happens twice at the same position writes once.
 */
export const PROGRESS_FLUSH_DELTA_SEC = 0.5;

/**
 * Below this, there is nothing to resume to and nothing to draw.
 *
 * It bounds what is *shown*, not what is stored — a smaller position is still written, because that is
 * how "start from the beginning" takes effect: watching the first seconds again overwrites the old
 * playhead, and the entry has to be able to hold the small number that results. Found in a browser:
 * pressing *Start from the beginning* stored `positionSec: 0` correctly and then every feed row for that
 * video offered "Resume — you stopped at 0:00. Opening it continues from there", which is three
 * statements and none of them true.
 */
export const RESUME_MIN_POSITION_SEC = 10;

/**
 * Has this video been watched to the end?
 *
 * **The two thresholds are combined by taking whichever is later**, and getting that backwards breaks
 * one end of the corpus or the other:
 *
 * - A **40-second Short**: 15 seconds from the end is 25 s, which is 62 % — the tail alone would call a
 *   Short finished less than two thirds through. The fraction (38 s) is the honest one.
 * - A **three-hour stream**: 95 % is 2 h 51 m, leaving five minutes of content — the fraction alone
 *   would call it finished with a whole segment to go. The tail (2 h 59 m 45 s) is the honest one.
 *
 * So the answer is `position ≥ max(duration × FRACTION, duration − TAIL)`, i.e. the remaining time is
 * under `min(TAIL, duration × (1 − FRACTION))`. Neither arm alone covers both, which is the same shape
 * `shouldRecordWatched`'s disjunction has for the opposite end of a video.
 *
 * Guards on non-finite input rather than trusting the player: a `<video>` reports `NaN` before metadata
 * loads and `Infinity` on a live stream, and both would mark every video finished on arrival.
 */
export function progressComplete(positionSec: number, durationSec: number | null): boolean {
  if (!Number.isFinite(positionSec) || positionSec <= 0) return false;
  if (durationSec === null || !Number.isFinite(durationSec) || durationSec <= 0) return false;
  const completeAt = Math.max(
    durationSec * PROGRESS_COMPLETE_FRACTION,
    durationSec - PROGRESS_COMPLETE_TAIL_SEC,
  );
  return positionSec >= completeAt;
}

export interface ProgressSampleInput {
  readonly videoId: string;
  /** What the store already holds for this video, or `null`. */
  readonly previous: WatchProgressEntry | null;
  readonly positionSec: number;
  readonly durationSec: number | null;
  readonly nowEpochSec: number;
  /**
   * How far playback must have moved since the last write. {@link PROGRESS_WRITE_INTERVAL_SEC} while
   * sampling, {@link PROGRESS_FLUSH_DELTA_SEC} when flushing on a pause or a navigation.
   */
  readonly minDeltaSec: number;
}

/**
 * The entry to store for this sample, or `null` for "nothing to write".
 *
 * Three decisions, and the second is the one that is easy to get wrong:
 *
 * 1. **A first entry is only created once the playback is a watch**, and "a watch" is
 *    `shouldRecordWatched` — 30 seconds or 25 %, whichever comes first. Reusing §8's threshold rather
 *    than inventing a second one is deliberate: it means the videos with a progress bar are exactly the
 *    videos in the account's history, so the two features cannot disagree about what counts as watched,
 *    and a reader who glances at ten thumbnails does not fill this store with ten one-second entries.
 * 2. **An entry that already exists is updated at any position, including a smaller one.** This is what
 *    makes "start from the beginning" real: seeking to zero and watching writes the small number back,
 *    and the stale playhead is gone. Requiring the threshold on every write would leave a video the
 *    reader deliberately restarted still resuming at 40 minutes.
 * 3. **Nothing is written unless the position moved by `minDeltaSec`** — or the `complete` flag
 *    changed, which must land immediately: it is what stops the next visit resuming 15 seconds from the
 *    end, and it happens exactly once per video.
 */
export function progressSample(input: ProgressSampleInput): WatchProgressEntry | null {
  const { previous, positionSec, durationSec, nowEpochSec } = input;
  if (!Number.isFinite(positionSec) || positionSec < 0) return null;

  const complete = progressComplete(positionSec, durationSec);

  if (previous === null) {
    if (!shouldRecordWatched({ positionSec, durationSec })) return null;
  } else if (
    complete === previous.complete &&
    Math.abs(positionSec - previous.positionSec) < input.minDeltaSec
  ) {
    return null;
  }

  return {
    id: input.videoId,
    positionSec,
    durationSec,
    complete,
    cachedAtEpochSec: nowEpochSec,
    // Equal on purpose — see the header. This store's LRU order is "oldest position first", so a
    // render never has to write to storage to keep eviction meaningful.
    lastSeenEpochSec: nowEpochSec,
  };
}

/** Store a sample. A thin alias for {@link metaCachePut} so callers need only one policy constant. */
export function putWatchProgressEntry(
  store: WatchProgressStore,
  entry: WatchProgressEntry,
): WatchProgressStore {
  return metaCachePut(store, [entry], WATCH_PROGRESS_POLICY);
}

/**
 * What a row knows about its own progress: enough to draw a bar and to say a word about it.
 *
 * Separate from {@link WatchProgressEntry} because the two answer different questions. The entry is
 * storage — it carries the LRU fields and could be past the TTL. A mark is *usable*, which is why
 * expiry is resolved before one exists at all, and why `fraction` is here rather than being recomputed
 * by every renderer.
 */
export interface WatchProgressMark {
  readonly videoId: string;
  readonly positionSec: number;
  readonly durationSec: number | null;
  readonly complete: boolean;
  /**
   * 0–1, or `null` when there is no duration to divide by. A complete entry is `1` even when its
   * duration is unknown: "finished" is a fact the entry carries, not one derived from the ratio.
   */
  readonly fraction: number | null;
}

/**
 * One video's mark, or `null` when there is nothing usable to show.
 *
 * Per id rather than per list, deliberately. The alternative — hand in the page's ids and get a map
 * back — reads as the cheaper shape and is not: every listing model in the app would have to collect
 * its own ids before it could build its rows, and the two loops would have to stay in step. A lookup
 * closed over the store is one argument threaded through the models that already take `nowEpochSec`.
 *
 * `stale` is dropped rather than returned, which is this module's one departure from the cache it
 * shares its fold with — see the header.
 */
export function watchProgressMark(
  store: WatchProgressStore,
  videoId: string,
  nowEpochSec: number,
  policy: MetaCachePolicy = WATCH_PROGRESS_POLICY,
): WatchProgressMark | null {
  const lookup = metaCacheLookup(store, [videoId], nowEpochSec, policy);
  const entry = lookup.hits[0];
  if (entry === undefined) return null;
  if (lookup.stale.length > 0) return null;
  // The one gate for "is there anything to say about this video": a position too small to resume to is
  // also too small to draw, and both features get the answer from here rather than each deciding.
  if (!entry.complete && entry.positionSec < RESUME_MIN_POSITION_SEC) return null;

  return {
    videoId: entry.id,
    positionSec: entry.positionSec,
    durationSec: entry.durationSec,
    complete: entry.complete,
    fraction: progressFraction(entry),
  };
}

/** 0–1 for the bar. `null` where a bar would be a claim about a length nothing reported. */
function progressFraction(entry: WatchProgressEntry): number | null {
  if (entry.complete) return 1;
  const duration = entry.durationSec;
  if (duration === null || !Number.isFinite(duration) || duration <= 0) return null;
  return Math.min(1, Math.max(0, entry.positionSec / duration));
}

export interface ResumeInput {
  readonly mark: WatchProgressMark | null;
  /**
   * What the address bar asked for — `t=`, `start=`, a clip's start. **It wins.** A timestamp in a URL
   * is somebody's deliberate address: a shared link, a chapter, a comment's clickable timecode, and
   * silently overriding it with a stored playhead would break the one navigation the whole URL scheme
   * (§8) exists to make lossless.
   */
  readonly requestedStartSec: number | null;
  /** The reader's `save_player_pos`. `false` means no resume and no bars anywhere. */
  readonly enabled: boolean;
}

/**
 * Where to start, or `null` for "wherever the player would have started".
 *
 * **Live is not handled here and that is the point.** `stageForPlan` already refuses a start position
 * on a live plan — the seekable window has moved by the time anything lands, so the live edge is the
 * only time that still means the same thing (§7, and 0f measured it) — so feeding the resume in as *the
 * start position the route did not carry* inherits that rule instead of restating it. One consequence
 * worth knowing: the Watch page's "resumed at…" notice must be read back off the stage rather than from
 * this function, or a live stream would claim a resume that never happened.
 */
export function resumePosition(input: ResumeInput): number | null {
  if (!input.enabled) return null;
  if (input.requestedStartSec !== null) return null;

  const mark = input.mark;
  if (mark === null || mark.complete) return null;
  // Not dead code, and not a second threshold: `watchProgressMark` refuses to build a mark this small,
  // so this is what keeps the function total over a mark that came from somewhere else. One constant.
  if (mark.positionSec < RESUME_MIN_POSITION_SEC) return null;
  return mark.positionSec;
}
